<template>
  <div class="message-board-container">
    <el-card class="message-list-card">
      <template #header>
        <div class="card-header">
          <span>学生提问列表</span>
        </div>
      </template>
      <el-table v-loading="loading" :data="messages" style="width: 100%">
        <el-table-column prop="consultationId" label="咨询ID" width="80" />
        <el-table-column prop="studentId" label="学生ID" width="120" />
        <el-table-column prop="studentName" label="学生姓名" width="120" />
        <el-table-column prop="questionContent" label="问题内容" min-width="300" />
        <el-table-column prop="replyContent" label="回复内容" min-width="300">
          <template #default="scope">
            {{ scope.row.replyContent || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="time" label="时间" width="180" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status === '已回复' ? 'success' : 'danger'">
              {{ scope.row.status || '待回复' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleReply(scope.row)">
              回复
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 回复对话框 -->
    <el-dialog v-model="replyDialogVisible" title="回复学生提问" width="50%">
      <div class="dialog-content">
        <div class="question-info">
          <p><span class="info-label">提问学生：</span>{{ currentMessage.studentName }} ({{ currentMessage.studentId }})</p>
          <p><span class="info-label">提问时间：</span>{{ currentMessage.time }}</p>
          <p><span class="info-label">提问内容：</span>{{ currentMessage.questionContent }}</p>
          <p v-if="currentMessage.replyContent"><span class="info-label">已回复内容：</span>{{ currentMessage.replyContent }}</p>
        </div>
        <el-divider />
        <el-form :model="replyForm">
          <el-form-item label="回复内容" prop="content" :rules="[{ required: true, message: '请输入回复内容', trigger: 'blur' }]">
            <el-input v-model="replyForm.content" type="textarea" placeholder="请输入回复内容" :rows="6" />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="replyDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitReply">提交回复</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';

// 留言数据
const messages = ref([]);
const loading = ref(false);
const unansweredCount = ref(0);

// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});

// 回复对话框
const replyDialogVisible = ref(false);
const currentMessage = ref({});
const replyForm = reactive({
  content: ''
});

// 获取留言列表
const fetchMessages = async () => {
  loading.value = true;
  try {
    // 后端API不接受分页参数，直接请求所有数据
    const response = await request.get('/consultation/teacher/list');
    messages.value = response.data;
    pagination.total = messages.value.length;
    unansweredCount.value = messages.value.filter(msg => !msg.replyContent || msg.replyContent.trim() === '').length;
  } catch (error) {
    ElMessage.error('获取留言列表失败');
    console.error('Failed to fetch messages:', error);
  } finally {
    loading.value = false;
  }
};

// 处理回复
const handleReply = (message) => {
  currentMessage.value = { ...message };
  replyForm.content = message.replyContent || '';
  replyDialogVisible.value = true;
};

// 提交回复
const submitReply = async () => {
  if (!replyForm.content.trim()) {
    ElMessage.warning('请输入回复内容');
    return;
  }

  try {
    // 根据后端API要求，使用正确的参数格式
    await request.post('/consultation/reply', null, {
      params: {
        consultationId: currentMessage.value.consultationId,
        replyContent: replyForm.content
      }
    });
    ElMessage.success('回复成功');
    replyDialogVisible.value = false;
    
    // 在刷新列表前，先更新当前消息的状态为已回复
    if (currentMessage.value && currentMessage.value.consultationId) {
      const messageIndex = messages.value.findIndex(msg => msg.consultationId === currentMessage.value.consultationId);
      if (messageIndex !== -1) {
        messages.value[messageIndex].status = 'answered';
        messages.value[messageIndex].replyContent = replyForm.content;
        // 更新未回复数量
        unansweredCount.value = messages.value.filter(msg => !msg.replyContent || msg.replyContent.trim() === '').length;
      }
    }
    
    fetchMessages(); // 刷新列表以确保数据最新
  } catch (error) {
    ElMessage.error('回复失败');
    console.error('Failed to submit reply:', error);
  }
};

// 分页处理
const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchMessages();
};

const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchMessages();
};

// 初始化
onMounted(() => {
  fetchMessages();
});
</script>

<style scoped>
.message-board-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.unanswered-badge {
  margin-left: 10px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.dialog-content {
  padding: 10px;
}

.question-info {
  margin-bottom: 10px;
}

.info-label {
  font-weight: bold;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>